// Cave84 — Investment Page (rebuilt 2026-07-04)
// Spec §4.7 F7.10–F7.13. Portfolio P&L vs actual purchase cost, per-vintage
// breakdown, region breakdown, needs-review surfacing.
//
// Rebuild rationale (owner review 2026-07-04):
//   1. Cost basis was hardcoded null → P&L always "—". Now joined from actual
//      in-cellar bottles (cost_eur_at_purchase), sharing the Inventory page's
//      ['bottles','all','in_cellar'] query cache — zero extra reads.
//   2. "Needs review" card was hardcoded '0'. Now reads the real
//      needs_owner_review queue (same source as PriceReviewPage).
//   3. €1 placeholder prices (written for unresolved lookups) were counted as
//      real market values. Now excluded from valuation and flagged for review.
//   4. Bottle counts come from actual bottles, not the denormalized
//      vintage.bottle_count (which can drift).
//   5. Restyled to the Bronze Nocturne design system (Fraunces / JetBrains
//      Mono / #141821 palette) matching Inventory + Dashboard.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Bottle, MarketPriceHistory, Vintage } from '@cave84/shared-types';
import { useAllVintages } from '@/hooks/useVintages';
import { useWines } from '@/hooks/useWines';
import { useProducersMap } from '@/hooks/useProducers';
import { useAppellationsMap } from '@/hooks/useAppellations';
import { usePriceReviewQueue } from '@/hooks/useMarketPrices';
import { useIsMobile } from '@/hooks/useIsMobile';
import { listAllBottles } from '@/services/bottles';
import { getLatestPricesForVintages } from '@/services/marketPrices';
import { Money } from '@/components/Money';

// ─── Palette (Bronze Nocturne) ────────────────────────────────────────────────

const C = {
  bg: '#141821', card: '#221F1A', panel: '#1B202B',
  hair: '#232936', border: '#3A3530',
  ink: '#ECE6D4', ink2: '#B8B0A2', ink3: '#9C9483', ink4: '#908977',
  gold: '#C49255', green: '#94A357', red: '#E07070', amber: '#C49255',
};

const MONO = "'JetBrains Mono',monospace";
const SERIF = "'Fraunces',serif";

// €1 placeholder entries (source: autoRefreshNewVintagePrice) are not market data.
const PLACEHOLDER_MAX = 1;

// ─── Types ────────────────────────────────────────────────────────────────────

interface VintageRow {
  vintage: Vintage;
  wineId: string;
  wineName: string;
  producerName: string;
  region: string;
  qty: number;                       // actual in-cellar bottles
  costTotal: number | null;          // sum of cost_eur_at_purchase (bottles with cost)
  costPerBottle: number | null;
  costedQty: number;                 // bottles that carry a cost
  marketPerBottle: number | null;    // latest real price (placeholders excluded)
  marketTotal: number | null;
  gainEur: number | null;            // marketTotal − costTotal (both required)
  gainPct: number | null;
  isPlaceholder: boolean;            // latest entry is the €1 review placeholder
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function gainColor(pct: number | null): string {
  if (pct === null) return C.ink4;
  return pct >= 0 ? C.green : C.red;
}

function pctLabel(pct: number | null): string {
  if (pct === null) return '—';
  return (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%';
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, color, to }: {
  label: string; value: React.ReactNode; sub?: React.ReactNode; color?: string; to?: string;
}) {
  const body = (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: '16px 18px', height: '100%', boxSizing: 'border-box' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.16em', color: C.ink3, fontWeight: 600, marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 500, letterSpacing: '-0.02em', color: color ?? C.ink, lineHeight: 1 }} dir="ltr">{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: C.ink4, marginTop: 7 }}>{sub}</div>}
    </div>
  );
  return to
    ? <Link to={to} style={{ textDecoration: 'none', display: 'block' }}>{body}</Link>
    : body;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type SortKey = 'gainPct' | 'gainEur' | 'marketTotal' | 'costTotal' | 'wineName';
type Filter = 'all' | 'gainers' | 'losers' | 'no_price' | 'review';

export default function InvestmentPage() {
  const isMobile = useIsMobile();
  const { data: allVintages = [], isLoading: loadingVintages } = useAllVintages();
  const { data: wines = [] } = useWines();
  const producersMap = useProducersMap();
  const appellationsMap = useAppellationsMap();
  const { data: reviewQueue = [] } = usePriceReviewQueue();

  const [sortKey, setSortKey] = useState<SortKey>('gainPct');
  const [sortAsc, setSortAsc] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');

  // Actual in-cellar bottles — source of truth for qty + cost basis.
  // Same query key as useInventory → shares the cache, no extra reads.
  const { data: bottles = [], isLoading: loadingBottles } = useQuery({
    queryKey: ['bottles', 'all', 'in_cellar'],
    queryFn: () => listAllBottles('in_cellar'),
    staleTime: 60_000,
  });

  const perVintage = useMemo(() => {
    const m = new Map<string, { qty: number; costSum: number; costedQty: number }>();
    for (const b of bottles as Bottle[]) {
      const e = m.get(b.vintage_id) ?? { qty: 0, costSum: 0, costedQty: 0 };
      e.qty += 1;
      if (typeof b.cost_eur_at_purchase === 'number' && b.cost_eur_at_purchase > 0) {
        e.costSum += b.cost_eur_at_purchase;
        e.costedQty += 1;
      }
      m.set(b.vintage_id, e);
    }
    return m;
  }, [bottles]);

  const wineMap = useMemo(() => new Map(wines.map(w => [w.id, w])), [wines]);

  const activeVintageIds = useMemo(
    () => allVintages.filter(v => perVintage.has(v.id)).map(v => v.id),
    [allVintages, perVintage]
  );

  const { data: pricesMap = new Map<string, MarketPriceHistory>(), isLoading: loadingPrices } = useQuery({
    queryKey: ['market_prices_bulk', activeVintageIds],
    queryFn: () => getLatestPricesForVintages(activeVintageIds),
    enabled: activeVintageIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // ── Rows ──────────────────────────────────────────────────────────────────
  const rows: VintageRow[] = useMemo(() => {
    return allVintages
      .filter(v => perVintage.has(v.id))
      .map(v => {
        const agg = perVintage.get(v.id)!;
        const wine = wineMap.get(v.wine_id);
        const producer = wine ? producersMap.get(wine.producer_id) : undefined;
        const appellation = wine ? appellationsMap.get(wine.appellation_id) : undefined;

        const latest = pricesMap.get(v.id) ?? null;
        const isPlaceholder = latest !== null && latest.price <= PLACEHOLDER_MAX;
        const marketPerBottle = latest !== null && !isPlaceholder ? latest.price : null;
        const marketTotal = marketPerBottle !== null ? marketPerBottle * agg.qty : null;

        const costTotal = agg.costedQty > 0 ? Math.round(agg.costSum) : null;
        const costPerBottle = agg.costedQty > 0 ? Math.round(agg.costSum / agg.costedQty) : null;

        // Honest P&L: compare market value of the costed bottles only, so a
        // vintage with partial cost data doesn't fake a gain on free bottles.
        let gainEur: number | null = null;
        let gainPct: number | null = null;
        if (marketPerBottle !== null && costTotal !== null && costTotal > 0) {
          const marketOfCosted = marketPerBottle * agg.costedQty;
          gainEur = Math.round(marketOfCosted - agg.costSum);
          gainPct = ((marketOfCosted - agg.costSum) / agg.costSum) * 100;
        }

        return {
          vintage: v,
          wineId: v.wine_id,
          wineName: wine?.cuvee_name ?? appellation?.name ?? 'Unknown wine',
          producerName: producer?.name ?? '—',
          region: appellation?.region ?? '—',
          qty: agg.qty,
          costTotal,
          costPerBottle,
          costedQty: agg.costedQty,
          marketPerBottle,
          marketTotal,
          gainEur,
          gainPct,
          isPlaceholder,
        };
      });
  }, [allVintages, perVintage, wineMap, producersMap, appellationsMap, pricesMap]);

  // ── Portfolio totals ──────────────────────────────────────────────────────
  const totals = useMemo(() => {
    let marketValue = 0, costBasis = 0, pnlMarket = 0, pnlCost = 0;
    let pricedVintages = 0, unpriced = 0, placeholders = 0, bottleCount = 0, pnlVintages = 0;
    for (const r of rows) {
      bottleCount += r.qty;
      if (r.costTotal !== null) costBasis += r.costTotal;
      if (r.marketTotal !== null) { marketValue += r.marketTotal; pricedVintages++; }
      else if (r.isPlaceholder) placeholders++;
      else unpriced++;
      if (r.gainEur !== null && r.marketPerBottle !== null && r.costTotal !== null) {
        pnlMarket += r.marketPerBottle * r.costedQty;
        pnlCost += r.costTotal;
        pnlVintages++;
      }
    }
    const pnlEur = Math.round(pnlMarket - pnlCost);
    const pnlPct = pnlCost > 0 ? ((pnlMarket - pnlCost) / pnlCost) * 100 : null;
    return { marketValue, costBasis, pnlEur, pnlPct, pnlVintages, pricedVintages, unpriced, placeholders, bottleCount, totalRows: rows.length };
  }, [rows]);

  const needsReviewCount = reviewQueue.length;

  // ── Region breakdown ──────────────────────────────────────────────────────
  const regionBreakdown = useMemo(() => {
    const m = new Map<string, { value: number; cost: number; bottles: number }>();
    for (const r of rows) {
      const e = m.get(r.region) ?? { value: 0, cost: 0, bottles: 0 };
      e.value += r.marketTotal ?? 0;
      e.cost += r.costTotal ?? 0;
      e.bottles += r.qty;
      m.set(r.region, e);
    }
    return Array.from(m.entries()).sort((a, b) => b[1].value - a[1].value).slice(0, 8);
  }, [rows]);

  // ── Filter + sort ─────────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    let result = rows;
    if (filter === 'gainers')  result = rows.filter(r => r.gainPct !== null && r.gainPct >= 0);
    if (filter === 'losers')   result = rows.filter(r => r.gainPct !== null && r.gainPct < 0);
    if (filter === 'no_price') result = rows.filter(r => r.marketPerBottle === null && !r.isPlaceholder);
    if (filter === 'review')   result = rows.filter(r => r.isPlaceholder);

    return [...result].sort((a, b) => {
      if (sortKey === 'wineName') {
        const cmp = a.producerName.localeCompare(b.producerName) || a.wineName.localeCompare(b.wineName);
        return sortAsc ? cmp : -cmp;
      }
      const av: number = a[sortKey] ?? -Infinity;
      const bv: number = b[sortKey] ?? -Infinity;
      return sortAsc ? av - bv : bv - av;
    });
  }, [rows, filter, sortKey, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(v => !v);
    else { setSortKey(key); setSortAsc(false); }
  };

  const isLoading = loadingVintages || loadingBottles || loadingPrices;

  const FILTERS: { key: Filter; label: string; count?: number }[] = [
    { key: 'all',      label: 'All' },
    { key: 'gainers',  label: 'Gainers' },
    { key: 'losers',   label: 'Losers' },
    { key: 'no_price', label: 'No price', count: totals.unpriced },
    { key: 'review',   label: '€1 review', count: totals.placeholders },
  ];

  const thBtn = (active: boolean): React.CSSProperties => ({
    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
    fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.14em',
    color: active ? C.gold : C.ink3, fontWeight: 600, textAlign: 'right',
  });

  const GRID = 'minmax(180px,2.2fr) 3rem 7.5rem 7.5rem 7rem';

  return (
    <div style={{ background: C.bg, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '0 16px 80px' : '0 32px 80px' }}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <section style={{ padding: isMobile ? '20px 0 16px' : '36px 0 24px', borderBottom: `1px solid ${C.hair}` }}>
          <div style={{ fontSize: 11, color: C.ink3, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: 8, fontWeight: 500 }}>Cave84 / Investment</div>
          <h1 style={{ fontFamily: SERIF, fontSize: isMobile ? 28 : 38, fontWeight: 400, letterSpacing: '-0.02em', lineHeight: 1, color: C.ink, margin: 0 }}>Portfolio</h1>
          <div style={{ color: C.ink3, fontSize: 13, marginTop: 8 }}>
            {totals.bottleCount} bottles · {totals.totalRows} vintages · {totals.pricedVintages} priced
          </div>
        </section>

        {/* ── Summary cards ──────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, padding: '20px 0' }}>
          <SummaryCard
            label="Market value"
            value={<Money value={totals.marketValue} currency="EUR" minWidthCh={7} />}
            sub={`${totals.pricedVintages} of ${totals.totalRows} vintages priced`}
            color={C.gold}
          />
          <SummaryCard
            label="Cost basis"
            value={<Money value={totals.costBasis} currency="EUR" minWidthCh={7} />}
            sub="sum of purchase costs"
          />
          <SummaryCard
            label="Unrealized P&L"
            value={
              <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                <Money value={totals.pnlEur} currency="EUR" minWidthCh={6} />
                <span style={{ fontFamily: MONO, fontSize: 13 }}>{pctLabel(totals.pnlPct)}</span>
              </span>
            }
            sub={`on ${totals.pnlVintages} vintages with cost + price`}
            color={gainColor(totals.pnlPct)}
          />
          <SummaryCard
            label="Needs review"
            value={String(needsReviewCount)}
            sub={needsReviewCount > 0 ? 'resolve in Price Review →' : 'all prices resolved'}
            color={needsReviewCount > 0 ? C.amber : C.ink}
            to="/prices/review"
          />
        </div>

        {/* ── Region breakdown ───────────────────────────────────────────── */}
        {regionBreakdown.length > 0 && (
          <div style={{ paddingBottom: 24 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.16em', color: C.ink3, fontWeight: 600, marginBottom: 10 }}>By region</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 10 }}>
              {regionBreakdown.map(([region, d]) => (
                <div key={region} style={{ background: C.panel, border: `1px solid ${C.hair}`, borderRadius: 4, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: C.ink3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 4 }}>{region}</div>
                  <div style={{ fontFamily: MONO, fontSize: 15, color: C.ink }} dir="ltr"><Money value={d.value} currency="EUR" minWidthCh={7} /></div>
                  <div style={{ fontSize: 11.5, color: C.ink4, marginTop: 2 }}>{d.bottles} btl · cost <Money value={d.cost} currency="EUR" minWidthCh={5} /></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Vintage table ──────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.16em', color: C.ink3, fontWeight: 600 }}>All vintages</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {FILTERS.map(f => (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)}
                style={{
                  padding: '5px 11px', borderRadius: 100, fontSize: 12, cursor: 'pointer',
                  border: `1px solid ${filter === f.key ? 'rgba(196,146,85,0.4)' : C.border}`,
                  background: filter === f.key ? 'rgba(196,146,85,0.08)' : 'transparent',
                  color: filter === f.key ? C.gold : C.ink2, fontWeight: filter === f.key ? 600 : 400,
                }}>
                {f.label}{typeof f.count === 'number' && f.count > 0 ? ` · ${f.count}` : ''}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div style={{ padding: '64px 20px', textAlign: 'center', color: C.ink4, fontSize: 14 }}>Loading portfolio…</div>
        ) : isMobile ? (
          /* ── Mobile cards ─────────────────────────────────────────────── */
          <div style={{ background: C.card, border: `1px solid ${C.border}` }}>
            {filteredRows.length === 0 && (
              <p style={{ padding: '40px 16px', textAlign: 'center', color: C.ink4, fontSize: 13 }}>No vintages match this filter.</p>
            )}
            {filteredRows.map(r => (
              <Link key={r.vintage.id} to={`/wines/${r.wineId}?vintage=${r.vintage.year}`} style={{ display: 'block', textDecoration: 'none', padding: '13px 16px', borderBottom: `1px solid ${C.hair}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: SERIF, fontSize: 14.5, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.producerName}</div>
                    <div style={{ fontSize: 11.5, color: C.ink3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.wineName} · <span style={{ fontFamily: MONO }}>{r.vintage.year}</span> · {r.qty} btl
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: MONO, fontSize: 13, color: C.ink }} dir="ltr">
                      {r.marketTotal !== null ? <Money value={r.marketTotal} currency="EUR" minWidthCh={6} /> : r.isPlaceholder ? '€1?' : '—'}
                    </div>
                    <div style={{ fontFamily: MONO, fontSize: 11, color: gainColor(r.gainPct), marginTop: 2 }}>{pctLabel(r.gainPct)}</div>
                  </div>
                </div>
                <div style={{ fontFamily: MONO, fontSize: 11.5, color: C.ink4, marginTop: 5 }} dir="ltr">
                  cost {r.costTotal !== null ? <Money value={r.costTotal} currency="EUR" minWidthCh={5} /> : '—'}
                  {r.gainEur !== null && <> · P&L <Money value={r.gainEur} currency="EUR" minWidthCh={5} /></>}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          /* ── Desktop table ────────────────────────────────────────────── */
          <div style={{ background: C.card, border: `1px solid ${C.border}` }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 14, padding: '12px 20px', borderBottom: `1px solid ${C.border}`, alignItems: 'center' }}>
              <button type="button" onClick={() => toggleSort('wineName')} style={{ ...thBtn(sortKey === 'wineName'), textAlign: 'left' }}>
                Producer / Wine {sortKey === 'wineName' && (sortAsc ? '↑' : '↓')}
              </button>
              <div style={{ ...thBtn(false), cursor: 'default', textAlign: 'right' }}>Btl</div>
              <button type="button" onClick={() => toggleSort('costTotal')} style={thBtn(sortKey === 'costTotal')}>
                Cost {sortKey === 'costTotal' && (sortAsc ? '↑' : '↓')}
              </button>
              <button type="button" onClick={() => toggleSort('marketTotal')} style={thBtn(sortKey === 'marketTotal')}>
                Market {sortKey === 'marketTotal' && (sortAsc ? '↑' : '↓')}
              </button>
              <button type="button" onClick={() => toggleSort('gainPct')} style={thBtn(sortKey === 'gainPct')}>
                P&L {sortKey === 'gainPct' && (sortAsc ? '↑' : '↓')}
              </button>
            </div>

            {filteredRows.length === 0 && (
              <p style={{ padding: '40px 20px', textAlign: 'center', color: C.ink4, fontSize: 13 }}>No vintages match this filter.</p>
            )}
            {filteredRows.map(r => (
              <Link key={r.vintage.id} to={`/wines/${r.wineId}?vintage=${r.vintage.year}`}
                style={{ display: 'grid', gridTemplateColumns: GRID, gap: 14, padding: '13px 20px', borderBottom: `1px solid ${C.hair}`, alignItems: 'center', textDecoration: 'none' }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = C.panel; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 500, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.producerName}</div>
                  <div style={{ fontSize: 11.5, color: C.ink3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.wineName} · <span style={{ fontFamily: MONO }}>{r.vintage.year}</span> · {r.region}
                  </div>
                </div>

                <div style={{ fontFamily: MONO, fontSize: 13, color: C.ink2, textAlign: 'right' }}>{r.qty}</div>

                <div style={{ textAlign: 'right' }} dir="ltr">
                  {r.costTotal !== null ? (
                    <>
                      <div style={{ fontFamily: MONO, fontSize: 13, color: C.ink2 }}><Money value={r.costTotal} currency="EUR" minWidthCh={6} /></div>
                      <div style={{ fontFamily: MONO, fontSize: 11, color: C.ink4, marginTop: 1 }}><Money value={r.costPerBottle ?? 0} currency="EUR" minWidthCh={4} />/btl{r.costedQty < r.qty ? ` · ${r.costedQty}/${r.qty}` : ''}</div>
                    </>
                  ) : <span style={{ color: C.ink4, fontSize: 13 }}>—</span>}
                </div>

                <div style={{ textAlign: 'right' }} dir="ltr">
                  {r.marketTotal !== null ? (
                    <>
                      <div style={{ fontFamily: MONO, fontSize: 13, color: C.ink, fontWeight: 500 }}><Money value={r.marketTotal} currency="EUR" minWidthCh={6} /></div>
                      <div style={{ fontFamily: MONO, fontSize: 11, color: C.ink4, marginTop: 1 }}><Money value={r.marketPerBottle ?? 0} currency="EUR" minWidthCh={4} />/btl</div>
                    </>
                  ) : r.isPlaceholder ? (
                    <span style={{ fontSize: 11, color: C.amber, border: `1px solid rgba(196,146,85,0.35)`, borderRadius: 100, padding: '2px 8px' }}>needs review</span>
                  ) : <span style={{ color: C.ink4, fontSize: 13 }}>—</span>}
                </div>

                <div style={{ textAlign: 'right' }} dir="ltr">
                  <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 500, color: gainColor(r.gainPct) }}>{pctLabel(r.gainPct)}</div>
                  {r.gainEur !== null && (
                    <div style={{ fontFamily: MONO, fontSize: 11, color: gainColor(r.gainPct), opacity: 0.75, marginTop: 1 }}>
                      <Money value={r.gainEur} currency="EUR" sign minWidthCh={4} />
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* ── Footnote ───────────────────────────────────────────────────── */}
        <p style={{ fontSize: 11.5, color: C.ink4, marginTop: 16, lineHeight: 1.6 }}>
          Market values from the latest price refresh; €1 placeholder entries are excluded from valuation and flagged for review.
          P&L compares market value against recorded purchase cost on bottles that carry cost data.{' '}
          <Link to="/prices/refresh" style={{ color: C.gold, textDecoration: 'none' }}>Run a refresh →</Link>{' '}
          <Link to="/prices/review" style={{ color: C.gold, textDecoration: 'none' }}>Price Review →</Link>
        </p>
      </div>
    </div>
  );
}
